// React Dependencies
import React, { Component } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Button, DialogTitle, DialogContent, DialogContentText, DialogActions, Dialog, Divider, Chip, Typography } from '@material-ui/core';
import { DropzoneArea } from 'material-ui-dropzone';
import { toast } from 'react-toastify';

// Internal Dependencies
import SortableMedia from '../global/SortableMedia/SortableMedia';
import FixedCropper from '../global/image/FixedCropper';
import OverLoading from '../global/OverLoading';
import Constants from '../../common/constants';
import { convertDataURLtoBlob } from '../../common/util';
import { IsEmpty } from '../../common/checks';
import PhotoDetailsView from '../photoDetailsView';

const useStyles = () => ({
  myDropZone: {
    position:        'relative',
    width:           '100%',
    height:          '50%',
    minHeight:       '50px',
    backgroundColor: '#f5f7fa',
    border:          0,
    boxShadow:       '0 1px 3px 1px rgba(0, 0, 0, .3)',
    borderRadius:    5,
    borderColor:     '#000000',
    cursor:          'pointer',
    boxSizing:       'border-box',
  },
});

class Photos extends Component {
  constructor(props) {
    super(props);
    this.selectCategoryRef = null;
    this.dropZoneRef = null;
    this.state = {
      mediaData:       '',
      alert:           false,
      loading:         true,
      openCropDialog:  false,
      originalFile:    null,
      currentFile:     null,
      src:             null,
      type:            null,
      uploadUrl:       '', // Blob Temp URL
      media:           [],
      maxFileSize:     7000000,
      imageFormats:    ['image/jpeg', 'image/jpg', 'image/png'],
      view:            false,
      viewImage:       '',
      cropping:        false,
      tempImgToDelete: [],
      entityId:        null,
    };
    this.minImageResWidth = 0;
    this.minImageResHeight = 0;

    this.handleSave = this.handleSave.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleSort = this.handleSort.bind(this);
    this.getBase64Image = this.getBase64Image.bind(this);
    this.handleView = this.handleView.bind(this);
    this.handleCrop = this.handleCrop.bind(this);
    this.handleUpload = this.handleUpload.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleUpdateEntity = this.handleUpdateEntity.bind(this);
    this.handleCloseCropper = this.handleCloseCropper.bind(this);
  }

  componentDidMount() {
    const fetchData = async () => {
      if (this.props.match && this.props.match.params && this.props.match.params.id) {
        try {
          const entity = await this.props.fetchEntity(this.props.match.params.id);
          if (entity) {
            this.setState({
              entityId: entity.id,
              media:    this.props.getMediaFromEntity(entity) || [],
              loading:  false,
            });
          }
        } catch (err) {
          toast.error(err.message || 'An unexpected error has occurred');
          this.setState({ loading: false });
        }
      }
    };
    fetchData();
  }

  handleUploadUrl = () => {
    this.setState({ openCropDialog: true, type: 'url', currentFile: null },
      () => {
        this.getBase64Image(this.state.uploadUrl, (base64image) => {
          const imgFile = this.b64toBlob(`data:image/*;base64,${base64image}`);
          this.setState({
            src:          `data:image/*;base64,${base64image}`,
            originalFile: new File([imgFile], 'o.jpeg', { type: 'image/jpeg' }),
          });
        });
      });
  }

  handleUpload = (files) => {
    if (files.length > 0) {
      this.setState({ openCropDialog: true, type: 'upload' }, () => {
        if (this.state.openCropDialog) {
          this.setState({ currentFile: files[files.length - 1] }, () => {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
              this.setState({ src: reader.result });
              const originalImageBlob = convertDataURLtoBlob(reader.result);
              this.setState({ originalFile: new File([originalImageBlob], 'o.jpeg', { type: 'image/jpeg' }) });
            });
            reader.readAsDataURL(this.state.currentFile);
          });
        }
      });
    }
  }

  getBase64Image = (imgUrl, callback) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      let dataURL = canvas.toDataURL('image/png');
      dataURL = dataURL.replace(/^data:image\/(png|jpg);base64,/, '');
      callback(dataURL); // the base64 string
    };
    img.setAttribute('crossOrigin', Constants.CrossOrigin);
    img.src = imgUrl;
  }

  b64toBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i += 1) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: 'image/jpeg' });
  }

  handleCrop = (image) => {
    const temp = [];
    this.setState({ uploadUrl: image.original, cropping: true }, () => {
      temp.push(image);
      this.props.onSelectImageToCrop(image);
      this.setState({ tempImgToDelete: temp }, () => {
        this.handleUploadUrl();
      });
    });
  }

  handleUploadMedia = async (formData, file) => {
    formData.append('image', this.state.originalFile);

    const [replacedImage] = this.state.tempImgToDelete;
    const isReplacingImage = !IsEmpty(replacedImage);
    if (isReplacingImage) {
      formData.append('original', replacedImage.original);
    }

    const result = await this.props.uploadMedia(this.state.entityId, formData, file);

    if (!IsEmpty(result)) {
      this.setState(
        (prevState) => ({
          openCropDialog: false,
          currentFile:    null,
          media:          isReplacingImage
            ? _.map(prevState.media, (img) => (img.original === replacedImage.original ? result : img))
            : prevState.media.concat(result),
        }),
        () => {
          this.handleCloseCropper();
          if (this.state.cropping === true && this.state.tempImgToDelete.length > 0) {
            toast.success('Image cropped successfully');
          } else {
            toast.success('Image uploaded successfully');
          }
        },
      );
    } else {
      this.handleCloseCropper();
      toast.error('An unexpected error has occurred');
    }
  }

  handleUpdateMedia = async (formData, file) => {
    if (!IsEmpty(this.props.getMediaId)) {
      const result = await this.props.updateMedia(this.state.entityId, formData, file);

      if (!IsEmpty(result)) {
        const resultId = this.props.getMediaId(result);

        this.setState(
          (prevState) => {
            const newMedia = _.map(
              prevState.media,
              (m) => (this.props.getMediaId(m) === resultId ? result : m),
            );
            return {
              media:          newMedia,
              openCropDialog: false,
              currentFile:    null,
            };
          },
          () => {
            this.handleCloseCropper();
            toast.success('Image updated successfully');
          },
        );
      } else {
        this.handleCloseCropper();
        toast.error('An unexpected error has occurred');
      }
    }
  }

  handleSave = async (file) => {
    try {
      if (!IsEmpty(file)) {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('imageType', Constants.ImageType.Photo);

        if (this.props.isUpdating) {
          await this.handleUpdateMedia(formData, file);
        } else {
          await this.handleUploadMedia(formData, file);
        }
      }
    } catch (err) {
      toast.error(IsEmpty(err.message) ? err : err.message);
    }
  }

  handleDelete = async () => {
    try {
      this.setState({ loading: true });
      if (this.state.media.length - 1 < this.props.minMediaCount) {
        toast.warn(`Minimum ${this.props.minMediaCount} photo required. Please upload another photo before deletion.`);
        return;
      }

      let tempImgToDeleteData = this.state.mediaData;

      if (this.state.cropping === true && this.state.tempImgToDelete.length > 0) {
        this.setState({ mediaData: tempImgToDeleteData });
        [tempImgToDeleteData] = this.state.tempImgToDelete;
      }

      const response = await this.props.deleteMedia(this.state.entityId, tempImgToDeleteData, Constants.FileType.Image);

      if (response) {
        const media = this.state.media.filter((image) => {
          if (image.display) {
            return image.display !== tempImgToDeleteData.display;
          } else {
            return image.original !== tempImgToDeleteData.original;
          }
        });

        toast.success('Image successfully deleted.');

        this.setState({
          media,
          alert:    false,
          cropping: false,
        });
      } else {
        this.setState({ alert: false });
        toast.error('An unexpected error has occurred');
      }
    } catch (err) {
      toast.error(err.message || err);
    } finally {
      this.setState({ loading: false });
    }
  }

  deleteConfirmation = (mediaData) => {
    this.setState({ alert: true, mediaData });
  }

  handleClose = () => {
    this.setState({
      openCropDialog:  false,
      cropping:        false,
      currentFile:     null,
      alert:           false,
      view:            false,
      type:            null,
      uploadUrl:       '',
      tempImgToDelete: [],
    });
  }

  handleCloseCropper = () => {
    this.handleClose();
    this.props.onCropperClose();
  }

  handleSort = (imagesData) => {
    this.setState({ media: imagesData });
  }

  handleView = (mediaData) => {
    if (IsEmpty(mediaData.poiId)) {
      this.setState({ view: true, viewImage: mediaData.original });
    } else {
      this.setState({
        view:      true,
        mediaData: {
          ...mediaData,
          photoUrl: mediaData.original,
        },
      });
    }
  }

  handleUpdateEntity = async (e) => {
    e.preventDefault();

    if (IsEmpty(this.props.updateEntity)) {
      return;
    }

    try {
      this.setState({ loading: true });
      const payload = { [this.props.mediaFieldName]: this.state.media };

      await this.props.updateEntity(this.state.entityId, payload);
      toast.success('Position updated');
    } catch (err) {
      toast.error(err.message || 'An error occurred');
    } finally {
      this.setState({ loading: false });
    }
  }

  handleErrorMessage = (file) => {
    if (!this.state.imageFormats.includes(file.type)) {
      toast.error('Only JPG, JPEG and PNG files are supported.');
      return;
    }
    if (file.size > this.state.maxFileSize) {
      toast.error('Maximum file size of 7MB exceeded.');
    }
  }

  render() {
    const { classes, title, isConfirmDisabled } = this.props;

    return (
      <div>
        {this.state.loading && <OverLoading />}
        <div
          className="form position-relative"
          onSubmit={(e) => this.onSubmit(e)}
          autoComplete="off"
        >
          {
            !this.props.disabled && (
              <div className="row" style={{ margin: '0px 0px 0px 0px' }}>
                <div className="col-md-3 col-sm-12 form-section" />
                <div className="col-md-6 col-sm-12 form-section" style={{ minHeight: 180 }}>
                  <DropzoneArea
                    dropzoneText={`(Res min=${this.minImageResWidth}x${this.minImageResHeight})`}
                    onChange={(file) => this.handleUpload(file)}
                    dropzoneClass={classes.myDropZone}
                    showPreviewsInDropzone={false}
                    showPreviews={false}
                    showAlerts={false}
                    filesLimit={1}
                    getDropRejectMessage={(file) => this.handleErrorMessage(file)}
                    maxFileSize={this.state.maxFileSize}
                    acceptedFiles={['image/*']}
                  />
                </div>
              </div>
            )
          }

          <div className="row">
            {this.state.media.length > 0
              ? (
                <div className="col-lg-12 col-sm-12 form-section">
                  <Typography
                    variant="h1"
                    className="text-left"
                    style={{
                      position:   'relative',
                      top:        20,
                      marginLeft: 63,
                    }}
                  >
                    {title}
                  </Typography>

                  <Chip style={{
                    backgroundColor: '#c0c0c0',
                    width:           `${this.props.itemWidth + 19}px`,
                    height:          `${this.props.itemHeight + 25}px`,
                    verticalAlign:   'bottom',
                    margin:          `-15px 0px -${this.props.itemHeight + 50}px 25px`,
                  }}
                  />

                  {
                    this.state.media
                      ? (
                        <SortableMedia
                          maxWd={this.props.itemWidth}
                          maxHt={this.props.itemHeight}
                          minWd={this.props.itemWidth}
                          minHt={this.props.itemHeight}
                          images={this.state.media}
                          handleCrop={this.handleCrop}
                          handleView={this.handleView}
                          handleDelete={(mediaData) => this.deleteConfirmation(mediaData)}
                          handleSort={this.handleSort}
                          disabled={this.props.disabled}
                          sortable={this.props.sortable}
                        />
                      )
                      : null
                  }
                </div>
              )
              : (
                <div className="col-lg-12 col-sm-12 form-section">
                  {
                    !this.props.disabled && (
                      <Divider />
                    )
                  }
                  <Typography style={{ marginLeft: 35 }} variant="overline">There are no images.</Typography>
                </div>
              )}
          </div>
          {
            (this.state.media.length > 0 && !IsEmpty(this.props.updateEntity))
              ? (
                <div className="row" style={{ margin: '40px 0px 0px 0px' }}>
                  <div className="col-lg-4 col-sm-12" />
                  <div className="col-lg-4 col-sm-12">
                    <div className="form-group">
                      <input
                        type="submit"
                        className="btn btn-primary block"
                        disabled={this.props.disabled}
                        value="Save Image Position"
                        onMouseDown={(e) => { this.handleUpdateEntity(e); }}
                      />
                    </div>
                  </div>
                </div>
              )
              : null
          }
        </div>
        <Dialog
          fullScreen
          open={this.state.openCropDialog}
          onClose={() => { this.handleCloseCropper(); }}
        >
          <FixedCropper
            label="Photo"
            disabled={isConfirmDisabled}
            minUploadPixelWidth={this.minImageResWidth}
            minUploadPixelHeight={this.minImageResHeight}
            width={16}
            height={9}
            cropPixelWidth={this.props.cropPixelWidth}
            cropPixelHeight={this.props.cropPixelHeight}
            outputPixelWidth={this.props.outputPixelWidth}
            outputPixelHeight={this.props.outputPixelHeight}
            type={this.state.type}
            url={this.state.uploadUrl}
            src={this.state.src}
            file={this.state.currentFile}
            handleSave={this.handleSave}
            handleClose={this.handleCloseCropper}
            isCropping={this.state.cropping}
            showForm={this.props.showForm}
            renderForm={this.props.renderForm}
          />
        </Dialog>
        <Dialog
          open={this.state.view}
          onClose={this.handleClose}
        >
          {
            !IsEmpty(this.state.mediaData.poiId) ? (
              <PhotoDetailsView
                open={this.state.view}
                photoId={this.state.mediaData.id}
                onClose={this.handleClose}
              />
            ) : (
              <>
                <DialogTitle id="alert-dialog-title">View Image</DialogTitle>
                <DialogContent>
                  <img
                    src={this.state.viewImage}
                    style={{
                      boxShadow:      '0 1px 3px 1px rgba(0, 0, 0, .3)',
                      borderRadius:   '10px',
                      backgroundSize: 'contain',
                    }}
                    alt="cover"
                  />
                </DialogContent>
              </>
            )
          }

          <DialogActions>
            <Button onClick={this.handleClose} color="primary">Close</Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={this.state.alert}
          onClose={() => { this.handleClose(); }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Remove Image</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete the image?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { this.handleClose(); }} color="primary">Cancel</Button>
            <Button onClick={() => { this.handleDelete(); }} color="primary">Confirm</Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

Photos.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,

  classes: PropTypes.shape({
    myDropZone: PropTypes.string,
  }).isRequired,

  isUpdating:        PropTypes.bool,
  isConfirmDisabled: PropTypes.bool,

  // Required functions from props
  fetchEntity:        PropTypes.func.isRequired,
  uploadMedia:        PropTypes.func.isRequired,
  deleteMedia:        PropTypes.func.isRequired,
  getMediaFromEntity: PropTypes.func.isRequired,
  updateEntity:       PropTypes.func,
  updateMedia:        PropTypes.func,
  getMediaId:         PropTypes.func, // could be photoId or original URL
  disabled:           PropTypes.bool,
  sortable:           PropTypes.bool,

  onSelectImageToCrop: PropTypes.func,
  onCropperClose:      PropTypes.func,

  // Configuration props
  mediaFieldName: PropTypes.string,
  title:          PropTypes.string,

  minMediaCount: PropTypes.number,
  showForm:      PropTypes.bool,
  renderForm:    PropTypes.func,

  cropPixelWidth:    PropTypes.number,
  cropPixelHeight:   PropTypes.number,
  outputPixelWidth:  PropTypes.number,
  outputPixelHeight: PropTypes.number,

  itemWidth:  PropTypes.number,
  itemHeight: PropTypes.number,

};

Photos.defaultProps = {
  mediaFieldName:      'media',
  title:               'Preview Image',
  minMediaCount:       0,
  showForm:            false,
  disabled:            false,
  sortable:            true,
  isUpdating:          false,
  updateEntity:        null,
  updateMedia:         null,
  getMediaId:          null,
  isConfirmDisabled:   false,
  renderForm:          () => {},
  cropPixelWidth:      400,
  cropPixelHeight:     225,
  outputPixelWidth:    800,
  outputPixelHeight:   450,
  itemWidth:           200,
  itemHeight:          112,
  onSelectImageToCrop: () => {},
  onCropperClose:      () => {},
};

export default withStyles(useStyles)(Photos);
